// src/components/SnackCalculator.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useUser } from "../../utensils/UserContext";
import { useConfig } from "../../utensils/ConfigContext";

const BASEROW_USERS_TABLE = 239437; // Tabla de usuarios en Baserow

const SnackCalculator = () => {
  const { user } = useUser();
  const { apiToken } = useConfig();

  if (!user || !user.Email) {
    return (
      <p className="text-danger">No se ha iniciado sesión correctamente.</p>
    );
  }

  // ¿Es admin?
  const isAdmin = useMemo(() => {
    const rol = String(user?.Rol ?? "")
      .toLowerCase()
      .trim();
    return rol === "1-administrador" || rol.includes("administrador");
  }, [user?.Rol]);

  const [showModal, setShowModal] = useState(false);

  // ===== Estado para búsqueda en Baserow =====
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [reloads, setReloads] = useState(0);

  // ===== Estado para correo manual (fallback) =====
  const [inputEmail, setInputEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  // ===== Correo efectivo que usa el iframe =====
  const [emailToUse, setEmailToUse] = useState(user.Email);

  // Admin: abrir modal al cargar
  useEffect(() => {
    if (isAdmin) setShowModal(true);
  }, [isAdmin]);

  useEffect(() => {
    setReloads(0);
  }, [emailToUse]);

  const validateEmail = (value) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).toLowerCase());

  // --- util: traer todas las páginas de una URL de Baserow (con ?next)
  const fetchAllPages = useCallback(
    async (url, accum = []) => {
      const res = await fetch(url, {
        headers: { Authorization: `Token ${apiToken}` },
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`Error Baserow (${res.status}): ${text}`);
      }
      const data = await res.json();
      const combined = [...accum, ...data.results];
      if (data.next) {
        return fetchAllPages(data.next, combined);
      }
      return combined;
    },
    [apiToken],
  );

  // Buscar por palabra clave (nombre, email, teléfono, etc.)
  const runSearch = useCallback(
    async (q) => {
      if (!apiToken) return;
      if (!q || !q.trim()) {
        setSearchResults([]);
        setSearchError("");
        return;
      }
      setSearching(true);
      setSearchError("");
      setSelectedRowId(null);
      try {
        const base = `https://api.baserow.io/api/database/rows/table/${BASEROW_USERS_TABLE}/?user_field_names=true&size=100`;
        const url = `${base}&search=${encodeURIComponent(q.trim())}`;
        const rows = await fetchAllPages(url);
        setSearchResults(rows);
      } catch (e) {
        setSearchError(e.message || "No se pudieron obtener resultados.");
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    },
    [apiToken, fetchAllPages],
  );

  // Debounce básico para la búsqueda
  useEffect(() => {
    if (!showModal) return;
    const t = setTimeout(() => {
      runSearch(searchQuery);
    }, 350);
    return () => clearTimeout(t);
  }, [searchQuery, showModal, runSearch]);

  // Confirmar selección / correo manual
  const handleConfirm = useCallback(() => {
    // 1) Si hay fila seleccionada, usamos su Email
    if (selectedRowId) {
      const row = searchResults.find((r) => r.id === selectedRowId);
      const email = row?.Email?.trim();
      if (email && validateEmail(email)) {
        setEmailToUse(email);
        setEmailError("");
        setShowModal(false);
        return;
      }
      setEmailError(
        "El registro seleccionado no tiene un correo válido. Selecciona otro o ingresa uno manualmente.",
      );
      return;
    }

    // 2) Si no hay selección, intentar con correo manual
    const value = inputEmail.trim();
    if (!validateEmail(value)) {
      setEmailError(
        "Selecciona un usuario de la lista o ingresa un correo electrónico válido.",
      );
      return;
    }
    setEmailToUse(value);
    setEmailError("");
    setShowModal(false);
  }, [selectedRowId, searchResults, inputEmail]);

  // Accesos de teclado en el modal
  useEffect(() => {
    if (!showModal) return;
    const onKey = (e) => {
      if (e.key === "Escape") setShowModal(false);
      if (e.key === "Enter") handleConfirm();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showModal, handleConfirm]);

  const encodedEmail = encodeURIComponent(emailToUse);
  const url = `https://fisiofitnessapp.com/calculator/index.html?antiguedad=0&parametro_email=${encodedEmail}`;

  // Helper para mostrar teléfono si existe (o usar Pin como posible campo)
  const getPhone = (row) => {
    return row?.Telefono || row?.Phone || row?.Celular || row?.Pin || "";
  };

  return (
    <>
      {/* Contenedor relativo: botón flotante no ocupa espacio */}
      <div
        style={{
          position: "relative",
          height: "80vh",
          border: "1px solid #ccc",
          borderRadius: "8px",
          overflow: "hidden",
        }}
      >
        <iframe
          src={url}
          title="Calculadora de valoración"
          style={{
            width: "100%",
            height: "100%",
            border: "none",
            // Oculto hasta completar 2 recargas
            visibility: reloads >= 2 ? "visible" : "hidden",
          }}
          onLoad={(e) => {
            // Cuenta cargas y fuerza dos recargas antes de mostrar
            const iframe = e.currentTarget;
            if (reloads < 2) {
              setReloads((r) => r + 1);
              // fuerza recarga (mismo comportamiento que presionar reload en el navegador)
              try {
                iframe.contentWindow?.location?.reload();
              } catch (_) {
                // fallback por si algún navegador bloquea el acceso (raro en mismo origen)
                iframe.src = url;
              }
            }
            // cuando reloads llegue a 2, el iframe queda visible
          }}
        />

        {reloads < 2 && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "rgba(255,255,255,.6)",
              fontWeight: 600,
            }}
          >
            Preparando datos…
          </div>
        )}

        {/* Botón flotante para abrir modal (no ocupa layout) */}
        <button
          onClick={() => {
            setInputEmail("");
            setEmailError("");
            setSearchQuery("");
            setSearchResults([]);
            setSelectedRowId(null);
            setShowModal(true);
          }}
          aria-label="Cambiar correo para la valoración"
          title="Cambiar correo"
          style={{
            position: "absolute",
            right: 12,
            bottom: 12,
            padding: "10px 14px",
            borderRadius: 9999,
            backgroundColor: "#f64012",
            color: "#fff",
            border: "none",
            boxShadow: "0 6px 14px rgba(0,0,0,.2)",
            cursor: "pointer",
          }}
        >
          Cambiar correo
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 50,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,.45)",
          }}
          onClick={(e) => {
            // Cierra si se hace click en el overlay (no en el cuadro)
            if (e.target.classList.contains("modal-overlay"))
              setShowModal(false);
          }}
        >
          <div
            style={{
              width: "100%",
              maxWidth: 720,
              borderRadius: 16,
              background: "#fff",
              boxShadow: "0 10px 30px rgba(0,0,0,.25)",
              padding: 20,
            }}
          >
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 6 }}>
              {isAdmin
                ? "Buscar persona y confirmar correo (Admin)"
                : "Buscar persona o ingresar correo"}
            </h3>
            <p style={{ fontSize: 14, color: "#6b7280", marginBottom: 16 }}>
              Escribe cualquier palabra clave (nombre, correo, teléfono, etc.).
              Selecciona un resultado (uno solo) o ingresa un correo
              manualmente.
            </p>

            {/* BUSCADOR */}
            <div
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                marginBottom: 12,
              }}
            >
              <input
                type="text"
                placeholder="Buscar por nombre, email, teléfono..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  flex: 1,
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: "10px 12px",
                  outline: "none",
                }}
              />
              <button
                onClick={() => runSearch(searchQuery)}
                style={{
                  padding: "10px 14px",
                  borderRadius: 10,
                  background: "#0d6efd",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                {searching ? "Buscando..." : "Buscar"}
              </button>
            </div>

            {/* RESULTADOS */}
            {searchError && (
              <div
                style={{
                  background: "#fef2f2",
                  color: "#991b1b",
                  border: "1px solid #fecaca",
                  borderRadius: 10,
                  padding: "10px 12px",
                  marginBottom: 12,
                }}
              >
                {searchError}
              </div>
            )}

            <div
              style={{
                maxHeight: 260,
                overflow: "auto",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                marginBottom: 12,
              }}
            >
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr
                    style={{
                      background: "#f8fafc",
                      borderBottom: "1px solid #e5e7eb",
                    }}
                  >
                    <th style={{ padding: 10, textAlign: "left" }}></th>
                    <th style={{ padding: 10, textAlign: "left" }}>Nombre</th>
                    <th style={{ padding: 10, textAlign: "left" }}>Email</th>
                    <th style={{ padding: 10, textAlign: "left" }}>Teléfono</th>
                    <th style={{ padding: 10, textAlign: "left" }}>Rol</th>
                    <th style={{ padding: 10, textAlign: "left" }}>Gimnasio</th>
                  </tr>
                </thead>
                <tbody>
                  {searchResults.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        style={{
                          padding: 12,
                          color: "#6b7280",
                          fontStyle: "italic",
                        }}
                      >
                        {searching
                          ? "Buscando..."
                          : "Sin resultados. Escribe y presiona Buscar."}
                      </td>
                    </tr>
                  ) : (
                    searchResults.map((r) => (
                      <tr key={r.id} style={{ borderTop: "1px solid #f1f5f9" }}>
                        <td style={{ padding: 8 }}>
                          <input
                            type="radio"
                            name="pick-user"
                            checked={selectedRowId === r.id}
                            onChange={() => setSelectedRowId(r.id)}
                          />
                        </td>
                        <td style={{ padding: 8 }}>{r.Nombre || "-"}</td>
                        <td style={{ padding: 8 }}>{r.Email || "-"}</td>
                        <td style={{ padding: 8 }}>{getPhone(r) || "-"}</td>
                        <td style={{ padding: 8 }}>{r.Rol || "-"}</td>
                        <td style={{ padding: 8 }}>{r.Gimnasio || "-"}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* CORREO MANUAL (fallback) */}
            <div style={{ marginTop: 6 }}>
              <label
                style={{
                  display: "block",
                  fontSize: 13,
                  color: "#374151",
                  marginBottom: 6,
                  fontWeight: 600,
                }}
              >
                O ingresa un correo manualmente
              </label>
              <input
                type="email"
                placeholder={user.Email}
                value={inputEmail}
                onChange={(e) => {
                  setInputEmail(e.target.value);
                  if (emailError) setEmailError("");
                }}
                style={{
                  width: "100%",
                  border: `1px solid ${emailError ? "#ef4444" : "#e5e7eb"}`,
                  borderRadius: 10,
                  padding: "10px 12px",
                  outline: "none",
                }}
              />
              {emailError && (
                <p style={{ marginTop: 8, fontSize: 13, color: "#ef4444" }}>
                  {emailError}
                </p>
              )}
            </div>

            {/* ACCIONES */}
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "flex-end",
                marginTop: 18,
              }}
            >
              <button
                onClick={() => {
                  // Si es admin y no selecciona nada ni escribe, usar su propio correo al cancelar
                  if (isAdmin && !selectedRowId && !inputEmail) {
                    setEmailToUse(user.Email);
                  }
                  setShowModal(false);
                }}
                style={{
                  padding: "8px 14px",
                  borderRadius: 10,
                  border: "1px solid #e5e7eb",
                  background: "#fff",
                  cursor: "pointer",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirm}
                style={{
                  padding: "8px 14px",
                  borderRadius: 10,
                  background: "#f64012",
                  color: "#fff",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Usar seleccionado / correo
              </button>
            </div>

            <div style={{ marginTop: 10, fontSize: 12, color: "#6b7280" }}>
              Tip: si dejas el campo de correo vacío y presionas <b>Cancelar</b>
              , se usará tu correo autenticado: <i>{user.Email}</i>.
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SnackCalculator;
